import { INITIATIVE_ENABLER_TYPES } from 'aint-types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

var LARGE_SCREEN_BREAKPOINT = "(min-width: 1350px)";

var IN_COLORS = {
    deepBlue: "#003781",
    forestGreen: "#087C4C",
    green: "#00C853",
    grey: "#808080",
    lightBlue: "#94CADC",
    navyBlue: "#003781",
    orchid: "#A464FB",
    white: "#FFFFFF",
    yellow: "#FAB600",
};

var EDITING_INITIATIVES_CURRENTLY_LOCKED_TOOLTIP = "This Initiative is currently locked by $1";
var REPORTING_IMPACT_CURRENTLY_LOCKED_TOOLTIP = "Reporting Impact for this initiative is currently locked by $1";

var FLOAT_REGEX = /^-?\d*?(?:[.]\d*?)?$/;

var ENABLERS = {
    PROCESS_MINING: INITIATIVE_ENABLER_TYPES[0],
    AUTOMATION: INITIATIVE_ENABLER_TYPES[1],
};

var FILTER_PARAMS = [
    "oeProjects",
    "functions",
    "LoBs",
    "programs",
    "enablers",
    "signOffStates",
    "baselines",
    "fromDate",
    "untilDate",
];

var ADMIN_GROUP_NAME = "admin";

var PLANNED_IMPACT_PROPERTIES = [
    "tieServiceExpensesImpactPlanned",
];
var ACTUAL_IMPACT_PROPERTIES = [
    "tieServiceExpensesImpactActual",
];
var IMPACT_PROPERTIES = ["tieServiceExpensesImpact"];

var IMPACT_SERIES_IDS_AND_LABELS = [
    {
        id: "tieServiceExpensesImpact",
        label: "TIE + Service Expenses Impact",
    },
    { id: "indemnityImpact", label: "Indemnity Impact" },
    { id: "toplineImpact", label: "Topline Impact" },
    { id: "commissionsImpact", label: "Commissions Impact" },
    { id: "internalFteImpact", label: "Internal FTE Impact" },
    { id: "externalFteImpact", label: "External FTE Impact" },
    { id: "investment", label: "Investment" },
];

var LOCK_RESOURCE_TEXTS = {
    resourceCurrentlyLocked: function (resource, email) {
        return "This ".concat(resource, " is locked to edit by \"").concat(email, "\"");
    },
};
var LOCK_RESOURCE_NAMES = {
    oeInitiatives: "oe-initiatives",
    initiative: "initiative",
    reportImpact: "report-impact",
    oeComments: "oe-comments",
    oeImpactPlan: "oe-impact-plan",
};

var NUMBER_TO_MONTH = [
    "jan",
    "feb",
    "mar",
    "apr",
    "may",
    "jun",
    "jul",
    "aug",
    "sep",
    "oct",
    "nov",
    "dec",
];
var MONTH_TO_NUMBER = {
    jan: 0,
    feb: 1,
    mar: 2,
    apr: 3,
    may: 4,
    jun: 5,
    jul: 6,
    aug: 7,
    sep: 8,
    oct: 9,
    nov: 10,
    dec: 11,
};

var CALCULATED_FIELD_KEYS = [
    "policies",
    "otherCostsOfTie",
    "totalOeFtes",
    "internalOeFtes",
    "externalOeFtes",
    "factorProductivityPolicies",
    "factorProductivityTbv",
    "tiePo",
    "tieOverTbv",
];
var CALCULATED_FIELDS = {
    policies: ["Q1-plan", "Q2-plan", "Q3-plan"],
    otherCostsOfTie: true,
    totalOeFtes: true,
    internalOeFtes: ["Q1-plan", "Q2-plan", "Q3-plan"],
    externalOeFtes: ["Q1-plan", "Q2-plan", "Q3-plan"],
    factorProductivityPolicies: true,
    factorProductivityTbv: true,
    tiePo: true,
    tieOverTbv: true,
};

var UNITS = {
    amount: "#",
    AmountCalculated: "# (calculated)",
    kAmount: "# k",
    kAmountCalculated: "# k (calculated)",
    kEur: "€ k",
    kEurCalculated: "€ k (calculated)",
};
var KPI_LABELS = {
    policies: {
        label: "Policies",
        unit: UNITS.kAmount,
    },
    tbv: {
        label: "TBV",
        unit: UNITS.kEur,
    },
    tie: {
        label: "TIE",
        unit: UNITS.kEur,
    },
    hrCostsOfTie: {
        label: "t/o Total HR costs portion of TIE",
        unit: UNITS.kEur,
    },
    otherCostsOfTie: {
        label: "t/o Other costs portion of TIE",
        unit: UNITS.kEurCalculated,
    },
    totalOeFtes: {
        label: "Total OE FTEs",
        unit: UNITS.kAmountCalculated,
    },
    internalOeFtes: {
        label: "t/o Total Internal FTEs*",
        unit: UNITS.kAmount,
    },
    externalOeFtes: {
        label: "t/o Total External FTEs*",
        unit: UNITS.kAmount,
    },
    operatingProfit: {
        label: "Operating Profit",
        unit: UNITS.kEur,
    },
    factorProductivityPolicies: {
        label: "Factor Productivity (Policies)",
        unit: UNITS.amount,
    },
    factorProductivityTbv: {
        label: "Factor Productivity (TBV)",
        unit: UNITS.amount,
    },
    tiePo: {
        label: "TIEPo",
        unit: UNITS.amountCalculated,
    },
    tieOverTbv: {
        label: "TIE over TBV",
        unit: UNITS.amountCalculated,
    },
    internalFteRapidProductivity: {
        label: "Total Internal FTEs in scope for Rapid Productivity",
        unit: UNITS.kAmount,
    },
    externalFteRapidProductivity: {
        label: "Total External FTEs in scope for Rapid Productivity",
        unit: UNITS.kAmount,
    },
    fteReductionRapidProductivity: {
        label: "FTE reduction from Rapid Productivity",
        unit: UNITS.kAmount,
    },
    internalFteReduction: {
        label: "Total Internal FTEs reduction",
        unit: UNITS.kAmount,
    },
    externalFteReduction: {
        label: "Total External FTEs reduction",
        unit: UNITS.kAmount,
    },
    externalFtes: {
        label: "Total External FTEs",
        unit: UNITS.kAmount,
    },
};

var OE_KPI_POPULATE = {
    policies: true,
    tbv: true,
    tie: true,
    operatingProfit: true,
    hrCostsOfTie: true,
    otherCostsOfTie: true,
    totalOeFtes: true,
    internalOeFtes: true,
    externalOeFtes: true,
    factorProductivityPolicies: true,
    factorProductivityTbv: true,
    tiePo: true,
    tieOverTbv: true,
    internalFteRapidProductivity: true,
    externalFteRapidProductivity: true,
    fteReductionRapidProductivity: true,
    internalFteReduction: true,
    externalFteReduction: true,
    externalFtes: true,
};

var OES_WITH_DEPENDENT_FIELDS = {
    "Allianz Partners Beta Test": [
        "interOeRegion",
        "businessUnit",
        "category",
        "oeProgram",
    ],
    "Allianz Partners": [
        "interOeRegion",
        "businessUnit",
        "category",
        "oeProgram",
    ],
    "Allianz Technology": ["interOeRegion"],
    "Allianz Services": ["interOeRegion"],
    "Allianz Trade": ["interOeRegion"],
    "Allianz Global Corporate & Specialty (AGCS)": ["interOeRegion"],
};
var OES_WITH_EXTENDED_LINE_OF_BUSINESS_OPTIONS = [
    "Allianz Partners",
    "Allianz Partners Beta Test",
];

var QUARTERS = ["Q1", "Q2", "Q3", "Q4"];

/**
 * Applies FX rates to initiatives, converting their impact values to EUR if needed.
 * For quarterly months (March, June, September, December),
 * applies special logic: (value_this_month - value_last_month) / rate_this_month + converted_value_previous_quarter
 */
function applyFxRatesToInitiatives(initiatives, fxRates) {
    var _a;
    // Quarterly month indices (0-based): March=2, June=5, September=8, December=11
    var quarterlyMonths = [2, 5, 8, 11];
    var _loop_1 = function (initiative) {
        var rate = fxRates.find(function (r) { return r.currency === initiative.currency; });
        ((_a = initiative.applicableImpactSeries) !== null && _a !== void 0 ? _a : []).forEach(function (impactSeries) {
            var _a, _b, _c, _d, _e;
            for (var _i = 0, _f = ["Planned", "Actual"]; _i < _f.length; _i++) {
                var suffix = _f[_i];
                var key = "".concat(impactSeries).concat(suffix);
                if (key === "externalFteImpactActual" ||
                    key === "externalFteImpactPlanned" ||
                    key === "internalFteImpactActual" ||
                    key === "internalFteImpactPlanned") {
                    continue;
                }
                if (Array.isArray(initiative[key])) {
                    var impacts = initiative[key];
                    // Sort impacts in-place by year and month
                    impacts.sort(function (a, b) {
                        var dateA = new Date(Number(a.year), a.month);
                        var dateB = new Date(Number(b.year), b.month);
                        return dateA.getTime() - dateB.getTime();
                    });
                    // To keep track of original values before conversion for each month
                    // We'll build a map: key = year-month, value = original value
                    var originalValues = {};
                    var _loop_2 = function (i) {
                        var impact = impacts[i];
                        var isQuarter = quarterlyMonths.includes(impact.month);
                        var rateValue = (_a = rate === null || rate === void 0 ? void 0 : rate.currencyValue.find(function (r) {
                            return r.month === impact.month && r.year.toString() === impact.year;
                        })) !== null && _a !== void 0 ? _a : { value: rate === null || rate === void 0 ? void 0 : rate.plannedRate };
                        var ymKey = "".concat(impact.year, "-").concat(impact.month);
                        originalValues[ymKey] = impact.value;
                        if (isQuarter) {
                            // Find previous quarter's impact
                            var prevQuarterMonth_1 = impact.month - 3;
                            var prevQuarterYear_1 = impact.year;
                            if (prevQuarterMonth_1 < 0) {
                                prevQuarterMonth_1 += 12;
                                prevQuarterYear_1 = (Number(impact.year) - 1).toString();
                            }
                            var prevImpact = impacts.find(function (imp) {
                                return imp.month === prevQuarterMonth_1 &&
                                    imp.year.toString() === prevQuarterYear_1;
                            });
                            if (prevImpact) {
                                // Get original value for previous quarter (before conversion)
                                var prevOrigKey = "".concat(prevQuarterYear_1, "-").concat(prevQuarterMonth_1);
                                var prevOriginalValue = (_b = originalValues[prevOrigKey]) !== null && _b !== void 0 ? _b : prevImpact.value;
                                // Get already converted value for previous quarter (after conversion)
                                var prevConvertedValue = prevImpact.value;
                                impact.value =
                                    (impact.value - prevOriginalValue) / ((_c = rateValue.value) !== null && _c !== void 0 ? _c : 1) +
                                        prevConvertedValue;
                            }
                            else {
                                // If previous quarter does not exist, treat as normal month
                                impact.value = impact.value / ((_d = rateValue.value) !== null && _d !== void 0 ? _d : 1);
                            }
                        }
                        else {
                            impact.value = impact.value / ((_e = rateValue.value) !== null && _e !== void 0 ? _e : 1);
                        }
                    };
                    for (var i = 0; i < impacts.length; i++) {
                        _loop_2(i);
                    }
                }
            }
        });
    };
    for (var _i = 0, initiatives_1 = initiatives; _i < initiatives_1.length; _i++) {
        var initiative = initiatives_1[_i];
        _loop_1(initiative);
    }
}

function convertCamelAndKebabCaseToTitle(str) {
    // Split the string by dots for nested objects
    var splittedString = str.split(".");
    // Handle camelCase and kebab-case
    var result = splittedString[splittedString.length - 1]
        .replace(/([a-z])([A-Z])/g, "$1 $2") // Handle camelCase (add space before uppercase letters)
        .replace(/-/g, " - ") // Replace hyphens with spaces around them
        .replace(/\s+/g, " "); // Remove any extra spaces that may have been added
    // Capitalize the first letter of each word
    var capitalizedResult = result
        .split(" ")
        .map(function (word) { return word.charAt(0).toUpperCase() + word.slice(1); })
        .join(" ");
    return capitalizedResult;
}

function mapCommentsToYearAndQuarter(comments) {
    var commentsByYearAndQuarter = {};
    comments.forEach(function (comment) {
        var year = comment.year, quarter = comment.quarter;
        if (!commentsByYearAndQuarter[year]) {
            commentsByYearAndQuarter[year] = {};
        }
        commentsByYearAndQuarter[year][quarter] = comment;
    });
    return commentsByYearAndQuarter;
}
function mapCommentsToArray(comments) {
    return Object.values(comments)
        .map(function (commentsByQuarter) { return Object.values(commentsByQuarter); })
        .flat()
        .filter(function (comment) { return comment.content != null && comment.content !== ""; });
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

function convertImpactValuesToArray(impactValues) {
    var MONTH = {
        jan: 0,
        feb: 1,
        mar: 2,
        apr: 3,
        may: 4,
        jun: 5,
        jul: 6,
        aug: 7,
        sep: 8,
        oct: 9,
        nov: 10,
        dec: 11,
    };
    if (impactValues == undefined) {
        return [];
    }
    var impactArray = [];
    Object.entries(impactValues).forEach(function (_a) {
        var year = _a[0], impactByMonth = _a[1];
        Object.entries(impactByMonth).forEach(function (_a) {
            var month = _a[0], impact = _a[1];
            var monthNumber = MONTH[month];
            if (impact != null && impact !== "") {
                var numberValue = Number(impact);
                if (!Number.isNaN(numberValue)) {
                    impactArray.push({
                        value: numberValue,
                        month: monthNumber,
                        year: year,
                    });
                }
            }
        });
    });
    return impactArray;
}
function convertImpactValuesToMap(impact) {
    var MONTHS = [
        "jan",
        "feb",
        "mar",
        "apr",
        "may",
        "jun",
        "jul",
        "aug",
        "sep",
        "oct",
        "nov",
        "dec",
    ];
    if (!impact || impact.length === 0) {
        return {};
    }
    return impact.reduce(function (acc, impact) {
        if (!impact.year || impact.month == undefined) {
            return acc;
        }
        if (!acc[impact.year]) {
            acc[impact.year] = {};
        }
        acc[impact.year][MONTHS[impact.month]] = impact.value.toString();
        return acc;
    }, {});
}
function convertImpactValuesUnit(_a) {
    var _b;
    var unit = _a.unit, initiative = _a.initiative, _c = _a.plannedOrActualArray, plannedOrActualArray = _c === void 0 ? ["Planned", "Actual"] : _c;
    if (!initiative) {
        return;
    }
    var impactSeries = __spreadArray([], ((_b = initiative.applicableImpactSeries) !== null && _b !== void 0 ? _b : []), true);
    if (impactSeries.includes("tieServiceExpensesImpact") &&
        initiative.tieServiceExpensesIncludesHrCosts) {
        impactSeries.push("hrCostImpact");
    }
    impactSeries.forEach(function (impactType) {
        // don't convert FTE impact
        if (["externalFteImpact", "internalFteImpact"].includes(impactType)) {
            return;
        }
        plannedOrActualArray.forEach(function (plannedOrActual) {
            var impactSeries = initiative[(impactType + plannedOrActual)];
            if (!isMonthlyImpactMap(impactSeries)) {
                return;
            }
            Object.entries(impactSeries !== null && impactSeries !== void 0 ? impactSeries : []).forEach(function (_a) {
                var year = _a[0], impactByMonth = _a[1];
                Object.entries(impactByMonth).forEach(function (_a) {
                    var month = _a[0], value = _a[1];
                    var impactValue = Number.parseFloat(value);
                    if (Number.isNaN(impactValue)) {
                        return;
                    }
                    impactSeries[year][month] = (unit === "EUR" ? impactValue * 1000 : impactValue / 1000).toString();
                });
            });
        });
    });
    return initiative;
}
function isMonthlyImpactMap(impact) {
    return !Array.isArray(impact);
}

function countOccurrences(property, values, array) {
    var result = Object.fromEntries(values.map(function (value) { return [value, 0]; }));
    for (var _i = 0, _a = array !== null && array !== void 0 ? array : []; _i < _a.length; _i++) {
        var item = _a[_i];
        var propertyValue = item === null || item === void 0 ? void 0 : item[property];
        for (var _b = 0, values_1 = values; _b < values_1.length; _b++) {
            var value = values_1[_b];
            if (typeof value === "string" &&
                (typeof propertyValue === "string" || Array.isArray(propertyValue)) &&
                propertyValue.includes(value)) {
                result[value]++;
            }
        }
    }
    return result;
}

dayjs.extend(utc);
function createEmptyInitiative() {
    return {
        uid: "",
        initiativeName: "",
        initiativeDescription: null,
        initiativePhase: null,
        itChangeRequired: false,
        itProjectId: "",
        oeProgram: false,
        oeProgramNr: "",
        implementingBusinessUnit: "",
        primaryFunction: null,
        primarySubFunction: "",
        secondaryFunctions: [],
        primaryLineOfBusiness: null,
        primaryLineOfBusinessOther: "",
        commercialLineOfBusiness: null,
        secondaryLineOfBusiness: [],
        secondaryLineOfBusinessOther: "",
        category: null,
        initiativeOwner: [],
        implementationProgram: null,
        implementationProgramOther: "",
        primaryImpactDriver: [],
        tieServiceExpensesImpactPlanned: {},
        indemnityImpactPlanned: {},
        toplineImpactPlanned: {},
        commissionsImpactPlanned: {},
        internalFteImpactPlanned: {},
        externalFteImpactPlanned: {},
        investmentPlanned: {},
        hrCostImpactPlanned: {},
        tieServiceExpensesIncludesHrCosts: false,
        initiativeEnablers: [],
        processMiningEnablementType: [],
        processMiningEnablementTypeOther: "",
        automationAIPowered: false,
        automationGenAIPowered: false,
        automationComplexity: null,
        automationInvolvedSystem: "",
        automationMethodology: [],
        automationMethodologyOther: "",
        automationSolutionImplementationProvider: null,
        automationTechnicalSolution: "",
        automationUnderlyingPlatform: [],
        relatedKPIDashboardHyperlink: "",
        startDate: dayjs().utc().startOf("month").toString(),
        endDate: dayjs().endOf("month").toString(),
        monitoringEndDate: dayjs().endOf("month").toString(),
        oneOffInvestment: null,
        baselineYear: "",
        signOffCoo: null,
        signOffCfo: null,
        signOffChro: null,
        affectedTeam: "",
        applicableImpactSeries: [
            "tieServiceExpensesImpact",
            "indemnityImpact",
            "toplineImpact",
            "commissionsImpact",
            "internalFteImpact",
            "externalFteImpact",
            "investment",
        ],
        localInitiativeCluster: "",
        initiativeSponsors: "",
        interOeRegion: null,
        businessUnit: [],
        supportingDocuments: [],
        impactComment: "",
    };
}

function filterInitiatives(_a, initiatives) {
    var searchQueryFilter = _a.searchQueryFilter, yearFilter = _a.yearFilter, updateStatusFilter = _a.updateStatusFilter, phaseFilter = _a.phaseFilter, lineOfBusinessFilter = _a.lineOfBusinessFilter, businessUnitFilter = _a.businessUnitFilter, categoryFilter = _a.categoryFilter;
    return initiatives.filter(function (initiative) {
        var _a, _b;
        if (searchQueryFilter && searchQueryFilter !== "") {
            var search_1 = searchQueryFilter.toLowerCase();
            if (!initiative.uid.toLowerCase().includes(search_1) &&
                !initiative.initiativeName.toLowerCase().includes(search_1) &&
                ((_a = initiative.initiativeOwner) !== null && _a !== void 0 ? _a : []).findIndex(function (owner) {
                    return owner.toLowerCase().includes(search_1);
                }) === -1) {
                return false;
            }
        }
        // Year filter
        if (yearFilter && yearFilter !== "") {
            var yearFilterNum = Number.parseInt(yearFilter);
            var startYear = initiative.startDate
                ? dayjs(initiative.startDate).year()
                : undefined;
            var endYear = initiative.endDate
                ? dayjs(initiative.endDate).year()
                : undefined;
            if (!startYear && !endYear) {
                return false;
            }
            if ((!startYear && endYear && endYear !== yearFilterNum) ||
                (startYear && !endYear && startYear !== yearFilterNum)) {
                return false;
            }
            if (startYear && endYear) {
                if (startYear > yearFilterNum || endYear < yearFilterNum) {
                    return false;
                }
            }
        }
        if (updateStatusFilter &&
            updateStatusFilter.length > 0 &&
            (initiative.updateStatus == null ||
                !updateStatusFilter.includes(initiative.updateStatus))) {
            return false;
        }
        if (phaseFilter &&
            phaseFilter.length > 0 &&
            (initiative.initiativePhase == null ||
                !phaseFilter.includes(initiative.initiativePhase))) {
            return false;
        }
        if (lineOfBusinessFilter &&
            lineOfBusinessFilter.length > 0 &&
            initiative.primaryLineOfBusiness &&
            !lineOfBusinessFilter.includes(initiative.primaryLineOfBusiness) &&
            initiative.secondaryLineOfBusiness &&
            !initiative.secondaryLineOfBusiness.some(function (secondaryLob) {
                return lineOfBusinessFilter.includes(secondaryLob);
            })) {
            return false;
        }
        if (businessUnitFilter &&
            businessUnitFilter.length > 0 &&
            !((_b = initiative.businessUnit) !== null && _b !== void 0 ? _b : []).some(function (bu) {
                return businessUnitFilter.includes(bu);
            })) {
            return false;
        }
        if (categoryFilter &&
            categoryFilter.length > 0 &&
            (initiative.category == null ||
                !categoryFilter.includes(initiative.category))) {
            return false;
        }
        return true;
    });
}

function filterInitiativesOnDashboardPage(params) {
    if (!params.filterData) {
        return params.initiatives;
    }
    var initiatives = params.initiatives, filterData = params.filterData;
    return initiatives.filter(function (initiative) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        if (((_a = filterData.functionsFilter) === null || _a === void 0 ? void 0 : _a.length) &&
            !filterData.functionsFilter.includes((_b = initiative === null || initiative === void 0 ? void 0 : initiative.primaryFunction) !== null && _b !== void 0 ? _b : "")) {
            return false;
        }
        if (((_c = filterData.LoBsFilter) === null || _c === void 0 ? void 0 : _c.length) &&
            !filterData.LoBsFilter.includes((_d = initiative === null || initiative === void 0 ? void 0 : initiative.primaryLineOfBusiness) !== null && _d !== void 0 ? _d : "")) {
            return false;
        }
        if (((_e = filterData.programsFilter) === null || _e === void 0 ? void 0 : _e.length) &&
            !filterData.programsFilter.includes((_f = initiative === null || initiative === void 0 ? void 0 : initiative.implementationProgram) !== null && _f !== void 0 ? _f : "")) {
            return false;
        }
        if (((_g = filterData.enablersFilter) === null || _g === void 0 ? void 0 : _g.length) &&
            !filterData.enablersFilter.some(function (f) { var _a; return (_a = initiative.initiativeEnablers) === null || _a === void 0 ? void 0 : _a.map(function (fun) { return fun.toString(); }).includes(f); })) {
            return false;
        }
        if (((_h = filterData.baselinesFilter) === null || _h === void 0 ? void 0 : _h.length) &&
            !filterData.baselinesFilter.includes((_j = initiative.baselineYear) !== null && _j !== void 0 ? _j : "")) {
            return false;
        }
        if (((_k = filterData.fromDateFilter) === null || _k === void 0 ? void 0 : _k[0]) &&
            dayjs(initiative.startDate).isBefore(dayjs(filterData.fromDateFilter[0]))) {
            return false;
        }
        if (((_l = filterData.untilDateFilter) === null || _l === void 0 ? void 0 : _l[0]) &&
            dayjs(initiative.monitoringEndDate).isAfter(dayjs(filterData.untilDateFilter[0]))) {
            return false;
        }
        return true;
    });
}

function fireFilterEventWithTheProperPayloadBasedOnTrigger(trigger, filterData, event, fireEvent) {
    var filterKey = trigger;
    var filterArray = filterData[filterKey];
    if (typeof event === "string" &&
        (filterKey === "fromDateFilter" || filterKey === "untilDateFilter")) {
        filterData[filterKey] = [event];
    }
    else {
        var typeCastEvent_1 = event;
        if (filterArray && typeCastEvent_1.target) {
            if (!Array.isArray(filterArray)) {
                return;
            }
            if (typeCastEvent_1.target.checked) {
                filterArray.push(typeCastEvent_1.target.name);
            }
            else {
                filterData[filterKey] = filterArray.filter(function (uncheckedValue) { return uncheckedValue !== typeCastEvent_1.target.name; });
            }
        }
    }
    fireEvent === null || fireEvent === void 0 ? void 0 : fireEvent("filter:apply", filterData);
}

dayjs.extend(utc);
/** formats a given `string` or `Date` into a `string` with the format `YYYY-MM-DD"`
 * and adjusts it to the current timezone */
function formatDate(date, format) {
    if (format === void 0) { format = "YYYY-MM-DD"; }
    if (!date) {
        return "";
    }
    return dayjs(date).format(format);
}
function formatDateUTC(date, format, output) {
    if (format === void 0) { format = "YYYY-MM-DD"; }
    if (output === void 0) { output = "string"; }
    if (!date) {
        return "";
    }
    var dateObj = typeof date === "string" ? new Date(date) : date;
    var hours = dateObj.getUTCHours();
    if (hours >= 12) {
        dateObj.setUTCDate(dateObj.getUTCDate() + 1);
    }
    dateObj.setUTCHours(0, 0, 0, 0);
    return output === "dayjs"
        ? dayjs.utc(dateObj)
        : dayjs.utc(dateObj).format(format);
}

function getColorsAndTextForInitiativePhaseChips(phase) {
    switch (phase) {
        case "L0 - Idea":
            return {
                chipColor: "#dcebed",
                textColor: "#414141",
                label: "L0",
            };
        case "L1 - Identified":
            return {
                chipColor: "#a8d4dd",
                textColor: "#414141",
                label: "L1",
            };
        case "L2 - Validated":
            return {
                chipColor: "#016ca4",
                textColor: "#ffffff",
                label: "L2",
            };
        case "L3 - Execution Prep":
            return {
                chipColor: "#005585",
                textColor: "#ffffff",
                label: "L3",
            };
        case "L4 - Execution":
            return {
                chipColor: "#003075",
                textColor: "#ffffff",
                label: "L4",
            };
        case "L5 - Fully Realized":
            return {
                chipColor: "#0f264b",
                textColor: "#ffffff",
                label: "L5",
            };
    }
}

var FULL_YEAR_MONTHS = [
    "jan",
    "feb",
    "mar",
    "apr",
    "may",
    "jun",
    "jul",
    "aug",
    "sep",
    "oct",
    "nov",
    "dec",
];
function getImpactRangeForDates(startDate, endDate) {
    var startYear = startDate.year();
    var startMonth = startDate.month();
    var endYear = endDate.year();
    var endMonth = endDate.month();
    if (endYear - startYear === 0) {
        var months = [];
        for (var m = startMonth; m <= endMonth; m++) {
            months.push(FULL_YEAR_MONTHS[m]);
        }
        return [
            {
                year: startYear.toString(),
                months: months,
            },
        ];
    }
    var years = [];
    for (var year = startYear; year <= endYear; year++) {
        var months = [];
        if (year === startYear) {
            for (var m = startMonth; m <= 11; m++) {
                months.push(FULL_YEAR_MONTHS[m]);
            }
            years.push({
                year: year.toString(),
                months: months,
            });
        }
        else if (year === endYear) {
            for (var m = 0; m <= endMonth; m++) {
                months.push(FULL_YEAR_MONTHS[m]);
            }
            years.push({
                year: year.toString(),
                months: months,
            });
        }
        else {
            years.push({
                year: year.toString(),
                months: FULL_YEAR_MONTHS,
            });
        }
    }
    return years;
}

function getMonthAndYear(date, offset) {
    if (date === void 0) { date = new Date(); }
    if (offset === void 0) { offset = 0; }
    // get the dayjs date & quarter of the input date
    var dayjsDate = dayjs(date);
    var month = dayjsDate.month();
    // calculate the quarter and year based on the input date and the offset
    var calculatedDate = dayjsDate.month(month + offset);
    var calculatedYear = calculatedDate.year();
    var calculatedMonth = calculatedDate.month();
    return [calculatedMonth, calculatedYear.toString()];
}

function getQuarterAndYear(date, offset) {
    if (date === void 0) { date = new Date(); }
    if (offset === void 0) { offset = 0; }
    // get the dayjs date & quarter of the input date
    var dayjsDate = dayjs(date);
    var month = dayjsDate.month();
    // calculate the quarter and year based on the input date and the offset
    var calculatedDate = dayjsDate.month(month + offset);
    var calculatedYear = calculatedDate.year();
    var calculatedMonth = calculatedDate.month();
    return [calculatedMonth, calculatedYear.toString()];
}

function getUpdateChipColorBasedOnUpdateStatus(updateStatus) {
    switch (updateStatus) {
        case "ended":
        case "completed":
            return {
                icon: "mood-smile.svg",
                color: "#e6f9ed",
            };
        case "pending":
            return {
                icon: "mood-neutral.svg",
                color: "#fef3d6",
            };
        case "overdue":
            return {
                icon: "mood-sad.svg",
                color: "#fae7e7",
            };
    }
}

function hasImpactValue(_a) {
    var _b;
    var _c = _a.impactValues, impactValues = _c === void 0 ? {} : _c, month = _a.month, year = _a.year;
    return ((_b = impactValues === null || impactValues === void 0 ? void 0 : impactValues[year]) === null || _b === void 0 ? void 0 : _b[NUMBER_TO_MONTH[month]]) != null;
}

function whichActualsNeedToBeConfirmed(initiative, actualConfirmation) {
    var _a, _b, _c, _d;
    if (!initiative) {
        return [];
    }
    if (!actualConfirmation) {
        return Array.from((_a = initiative.applicableImpactSeries) !== null && _a !== void 0 ? _a : []);
    }
    var toBeConfirmed = [];
    var _e = getMonthAndYear(), currentMonth = _e[0], currentYear = _e[1];
    for (var _i = 0, _f = ((_b = initiative.applicableImpactSeries) !== null && _b !== void 0 ? _b : []); _i < _f.length; _i++) {
        var impactType = _f[_i];
        if (!actualConfirmation[impactType]) {
            toBeConfirmed.push(impactType);
            continue;
        }
        var lastConfirmedActual = dayjs(actualConfirmation[impactType]);
        var impactSeries = initiative[(impactType + "Actual")];
        var _g = (_c = Object.entries(impactSeries).find(function (_a) {
            var year = _a[0];
            return year === currentYear;
        })) !== null && _c !== void 0 ? _c : [undefined, undefined], impactYear = _g[0], impactByMonth = _g[1];
        var _h = (_d = Object.entries(impactByMonth !== null && impactByMonth !== void 0 ? impactByMonth : {}).find(function (_a) {
            var month = _a[0];
            return +month === currentMonth;
        })) !== null && _d !== void 0 ? _d : [undefined, undefined], impactMonth = _h[0], impact = _h[1];
        if (impact &&
            impact.length > 0 &&
            lastConfirmedActual.isBefore(dayjs("".concat(impactYear, "-").concat(impactMonth + 1, "-1")))) {
            toBeConfirmed.push(impactType);
        }
    }
    return toBeConfirmed;
}

function getUpdateStatus(originalInitiative) {
    var _a, _b, _c;
    var initiative = __assign({}, originalInitiative);
    ((_a = initiative.applicableImpactSeries) !== null && _a !== void 0 ? _a : []).forEach(function (impactType) {
        var impactKey = (impactType + "Actual");
        initiative[impactKey] = convertImpactValuesToMap(initiative[impactKey]);
    });
    // if the initiative is in the "Idea / Backlog" or "Done" phase, it's considered "completed"
    if (initiative.initiativePhase === "L0 - Idea" ||
        initiative.initiativePhase === "L5 - Fully Realized") {
        return "completed";
    }
    // initialize dayjs dates for easy comparison
    var currentDate = dayjs(new Date());
    var monitoringEndDate = dayjs(new Date((_b = initiative === null || initiative === void 0 ? void 0 : initiative.monitoringEndDate) !== null && _b !== void 0 ? _b : ""));
    var startDate = dayjs(new Date((_c = initiative === null || initiative === void 0 ? void 0 : initiative.startDate) !== null && _c !== void 0 ? _c : ""));
    // if the current date is before the start date or the initiative is in the first month of its lifecycle, it's considered "completed"
    if (currentDate.year() < startDate.year() ||
        (currentDate.year() === startDate.year() &&
            currentDate.month() <= startDate.month())) {
        return "completed";
    }
    // if the current date is after the end date it's considered "ended"
    if (currentDate.year() > monitoringEndDate.year() ||
        (currentDate.year() === monitoringEndDate.year() &&
            currentDate.month() > monitoringEndDate.month())) {
        return "ended";
    }
    var impactBasedUpdateStatus = getUpdateStatusBasedOnImpact(initiative);
    if (impactBasedUpdateStatus === "completed" &&
        whichActualsNeedToBeConfirmed(initiative, initiative.actualConfirmation)
            .length > 0) {
        return "pending";
    }
    return impactBasedUpdateStatus;
}
function getUpdateStatusBasedOnImpact(initiative) {
    var _a;
    var monthBeforeLast = getMonthAndYear(new Date(), -2);
    var startDate = dayjs(initiative.startDate);
    var startMonth = startDate.month();
    var startYear = startDate.year();
    var month = monthBeforeLast[0];
    var year = +monthBeforeLast[1];
    for (var _i = 0, _b = (_a = initiative.applicableImpactSeries) !== null && _a !== void 0 ? _a : []; _i < _b.length; _i++) {
        var impactType = _b[_i];
        var impactSeries = initiative[(impactType + "Actual")];
        while (year > startYear || (year === startYear && month >= startMonth)) {
            if (!hasImpactValue({
                impactValues: impactSeries,
                year: year.toString(),
                month: month,
            })) {
                return "overdue";
            }
            month--;
            if (month < 0) {
                month = 11;
                year--;
            }
        }
        var _c = getMonthAndYear(new Date(), -1), pastMonth = _c[0], pastYear = _c[1];
        if (!hasImpactValue({
            impactValues: impactSeries,
            month: pastMonth,
            year: pastYear.toString(),
        })) {
            return "pending";
        }
    }
    return "completed";
}

/**
 * Calculates the value of a given field for the given quarter and year.
 * Uses one of two calculation functions if the field value has to be calculated.
 */
function calculateValue(_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
    var field = _a.field, quarter = _a.quarter, year = _a.year, operatingEntity = _a.operatingEntity;
    var kpiObj = operatingEntity[field].find(function (oeKpi) { return +oeKpi.year === year && oeKpi.quarter === quarter; });
    var planValue = kpiObj === null || kpiObj === void 0 ? void 0 : kpiObj.planValue;
    var actualValue = kpiObj === null || kpiObj === void 0 ? void 0 : kpiObj.actualValue;
    if (CALCULATED_FIELD_KEYS.includes(field)) {
        var result = void 0;
        switch (field) {
            case "policies":
                if (quarter !== 4) {
                    planValue = +linearApproximation({
                        quarter: quarter,
                        year: year.toString(),
                        field: field,
                        operatingEntity: operatingEntity,
                    });
                }
                break;
            case "otherCostsOfTie":
                result = arithmeticCalculation({
                    year: year.toString(),
                    quarter: quarter,
                    operatingEntity: operatingEntity,
                    kpi1: "tie",
                    kpi2: "hrCostsOfTie",
                    operation: "-",
                });
                planValue = (_b = result.planValue) !== null && _b !== void 0 ? _b : 0;
                actualValue = (_c = result.actualValue) !== null && _c !== void 0 ? _c : 0;
                break;
            case "totalOeFtes":
                result = arithmeticCalculation({
                    year: year.toString(),
                    quarter: quarter,
                    operatingEntity: operatingEntity,
                    kpi1: "internalOeFtes",
                    kpi2: "externalOeFtes",
                    operation: "+",
                });
                planValue = (_d = result.planValue) !== null && _d !== void 0 ? _d : 0;
                actualValue = (_e = result.actualValue) !== null && _e !== void 0 ? _e : 0;
                break;
            case "internalOeFtes":
                if (quarter !== 4) {
                    planValue = +linearApproximation({
                        quarter: quarter,
                        year: year.toString(),
                        field: field,
                        operatingEntity: operatingEntity,
                    });
                }
                break;
            case "externalOeFtes":
                if (quarter !== 4) {
                    planValue = +linearApproximation({
                        quarter: quarter,
                        year: year.toString(),
                        field: field,
                        operatingEntity: operatingEntity,
                    });
                }
                break;
            case "factorProductivityPolicies":
                result = arithmeticCalculation({
                    year: year.toString(),
                    quarter: quarter,
                    operatingEntity: operatingEntity,
                    kpi1: "policies",
                    kpi2: "totalOeFtes",
                    operation: "/",
                });
                planValue = (_f = result.planValue) !== null && _f !== void 0 ? _f : 0;
                actualValue = (_g = result.actualValue) !== null && _g !== void 0 ? _g : 0;
                break;
            case "factorProductivityTbv":
                result = arithmeticCalculation({
                    year: year.toString(),
                    quarter: quarter,
                    operatingEntity: operatingEntity,
                    kpi1: "tbv",
                    kpi2: "totalOeFtes",
                    operation: "/",
                    useMultipliedQuarterlyValue: true,
                });
                planValue = (_h = result.planValue) !== null && _h !== void 0 ? _h : 0;
                actualValue = (_j = result.actualValue) !== null && _j !== void 0 ? _j : 0;
                break;
            case "tiePo":
                result = arithmeticCalculation({
                    year: year.toString(),
                    quarter: quarter,
                    operatingEntity: operatingEntity,
                    kpi1: "tie",
                    kpi2: "policies",
                    operation: "/",
                    useMultipliedQuarterlyValue: true,
                });
                planValue = (_k = result.planValue) !== null && _k !== void 0 ? _k : 0;
                actualValue = (_l = result.actualValue) !== null && _l !== void 0 ? _l : 0;
                break;
            case "tieOverTbv":
                result = arithmeticCalculation({
                    year: year.toString(),
                    quarter: quarter,
                    operatingEntity: operatingEntity,
                    kpi1: "tie",
                    kpi2: "tbv",
                    operation: "/",
                });
                planValue = (_m = result.planValue) !== null && _m !== void 0 ? _m : 0;
                actualValue = (_o = result.actualValue) !== null && _o !== void 0 ? _o : 0;
        }
    }
    return {
        planValue: roundToTwoDecimals(planValue),
        actualValue: roundToTwoDecimals(actualValue),
    };
}
function roundToTwoDecimals(value) {
    if (!value) {
        return;
    }
    return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}
/**
 * Does a simple arithmetic calculation (either add, subtract or divide) based on the given parameters.
 */
function arithmeticCalculation(_a) {
    var _b, _c, _d, _e;
    var year = _a.year, quarter = _a.quarter, operatingEntity = _a.operatingEntity, kpi1 = _a.kpi1, kpi2 = _a.kpi2, operation = _a.operation, _f = _a.useMultipliedQuarterlyValue, useMultipliedQuarterlyValue = _f === void 0 ? false : _f;
    var parseValue = function (value) {
        if (!value || Number.isNaN(value) || !Number.isFinite(value)) {
            return 0;
        }
        return value;
    };
    var kpi1Obj = operatingEntity[kpi1].find(function (kpi) { return kpi.year === year && kpi.quarter === quarter; });
    var kpi2Obj = operatingEntity[kpi2].find(function (kpi) { return kpi.year === year && kpi.quarter === quarter; });
    var kpi1PlanValue = (_b = kpi1Obj === null || kpi1Obj === void 0 ? void 0 : kpi1Obj.planValue) !== null && _b !== void 0 ? _b : 0;
    var kpi1ActualValue = (_c = kpi1Obj === null || kpi1Obj === void 0 ? void 0 : kpi1Obj.actualValue) !== null && _c !== void 0 ? _c : 0;
    var kpi2PlanValue = (_d = kpi2Obj === null || kpi2Obj === void 0 ? void 0 : kpi2Obj.planValue) !== null && _d !== void 0 ? _d : 0;
    var kpi2ActualValue = (_e = kpi2Obj === null || kpi2Obj === void 0 ? void 0 : kpi2Obj.actualValue) !== null && _e !== void 0 ? _e : 0;
    if (useMultipliedQuarterlyValue) {
        kpi1PlanValue = kpi1PlanValue * (4 / quarter);
        kpi1ActualValue = kpi1ActualValue * (4 / quarter);
    }
    switch (operation) {
        case "+":
            return {
                planValue: parseValue(kpi1PlanValue + kpi2PlanValue),
                actualValue: parseValue(kpi1ActualValue + kpi2ActualValue),
            };
        case "-":
            return {
                planValue: parseValue(kpi1PlanValue - kpi2PlanValue),
                actualValue: parseValue(kpi1ActualValue - kpi2ActualValue),
            };
        case "/":
            return {
                planValue: parseValue(kpi1PlanValue / kpi2PlanValue),
                actualValue: parseValue(kpi1ActualValue / kpi2ActualValue),
            };
    }
}
/**
 * Does a linear approximation using the previous years Q4 actual value as a starting point
 * and the current years Q4 plan value as the end point
 */
function linearApproximation(_a) {
    var _b, _c, _d, _e;
    var year = _a.year, quarter = _a.quarter, operatingEntity = _a.operatingEntity, field = _a.field;
    var value;
    var yearEndPlanValue = (_c = (_b = operatingEntity[field].find(function (kpi) { return kpi.year === year && kpi.quarter === 4; })) === null || _b === void 0 ? void 0 : _b.planValue) !== null && _c !== void 0 ? _c : 0;
    var previousYearEndActualValue = (_e = (_d = operatingEntity[field].find(function (kpi) { return +kpi.year === +year - 1 && kpi.quarter === 4; })) === null || _d === void 0 ? void 0 : _d.actualValue) !== null && _e !== void 0 ? _e : 0;
    value =
        ((yearEndPlanValue - previousYearEndActualValue) / 4) * quarter +
            +previousYearEndActualValue;
    if (Number.isNaN(value)) {
        value = "N.P.";
    }
    return value;
}

function sortString(a, b, direction) {
    a = a.toLowerCase();
    b = b.toLowerCase();
    if (a < b) {
        if (direction === "DESC") {
            return 1;
        }
        return -1;
    }
    if (a > b) {
        if (direction === "DESC") {
            return -1;
        }
        return 1;
    }
    return 0;
}
function sortByDate(a, b, direction) {
    if (!a) {
        if (direction === "DESC") {
            return 1;
        }
        return -1;
    }
    if (!b) {
        if (direction === "DESC") {
            return -1;
        }
        return 1;
    }
    var dateA = dayjs(a);
    var dateB = dayjs(b);
    if (dateA.isSame(dateB, "day")) {
        return 0;
    }
    if (dateA.isAfter(dateB, "day")) {
        if (direction === "DESC") {
            return -1;
        }
        return 1;
    }
    if (direction === "DESC") {
        return 1;
    }
    return -1;
}
var UPDATE_STATUS = {
    ended: 0,
    completed: 0,
    pending: 1,
    overdue: 2,
};
var SORT_BY_KEY = {
    uid: function (a, b, direction) {
        var uidA = Number(a.uid.substring(4));
        var uidB = Number(b.uid.substring(4));
        if (direction === "ASC") {
            return uidA - uidB;
        }
        return uidB - uidA;
    },
    initiativeName: function (a, b, direction) {
        return sortString(a.initiativeName, b.initiativeName, direction);
    },
    startDate: function (a, b, direction) {
        return sortByDate(a.startDate, b.startDate, direction);
    },
    monitoringEndDate: function (a, b, direction) {
        return sortByDate(a.monitoringEndDate, b.monitoringEndDate, direction);
    },
    realizedImpact: function (a, b, direction) {
        var _a, _b;
        var impactA = (_a = a.realizedImpact) !== null && _a !== void 0 ? _a : Infinity;
        var impactB = (_b = b.realizedImpact) !== null && _b !== void 0 ? _b : Infinity;
        return direction === "ASC" ? impactA - impactB : impactB - impactA;
    },
    forecastImpact: function (a, b, direction) {
        var _a, _b;
        var impactA = (_a = a.forecastImpact) !== null && _a !== void 0 ? _a : Infinity;
        var impactB = (_b = b.forecastImpact) !== null && _b !== void 0 ? _b : Infinity;
        return direction === "ASC" ? impactA - impactB : impactB - impactA;
    },
    initiativePhase: function (a, b, direction) {
        var phaseOrderA = a.initiativePhase
            ? +a.initiativePhase.charAt(1) // Extract the number after the L: L0 - Idea -> 0
            : Infinity;
        var phaseOrderB = b.initiativePhase
            ? +b.initiativePhase.charAt(1) // Extract the number after the L: L0 - Idea -> 0
            : Infinity;
        return direction === "ASC"
            ? phaseOrderA - phaseOrderB
            : phaseOrderB - phaseOrderA;
    },
    updateStatus: function (a, b, direction) {
        var statusA = a.updateStatus ? UPDATE_STATUS[a.updateStatus] : Infinity;
        var statusB = b.updateStatus ? UPDATE_STATUS[b.updateStatus] : Infinity;
        // If the status is the same, sort by the due date, since its displayed in the same column as the status
        if (statusA === statusB) {
            return sortByDate(a.dueDate, b.dueDate, direction);
        }
        return direction === "ASC" ? statusA - statusB : statusB - statusA;
    },
};
function sortInitiatives(_a, initiatives) {
    var key = _a.key, direction = _a.direction;
    return initiatives.sort(function (a, b) {
        var sortingFn = SORT_BY_KEY[key];
        if (!sortingFn) {
            return 0;
        }
        return sortingFn(a, b, direction);
    });
}

function sortInitiativesByUid(initiatives) {
    if (!initiatives) {
        return [];
    }
    return initiatives.sort(function (initiativeA, initiativeB) {
        var uidA = Number(initiativeA.uid.substring(4));
        var uidB = Number(initiativeB.uid.substring(4));
        return uidA - uidB;
    });
}

export { ACTUAL_IMPACT_PROPERTIES, ADMIN_GROUP_NAME, CALCULATED_FIELDS, CALCULATED_FIELD_KEYS, EDITING_INITIATIVES_CURRENTLY_LOCKED_TOOLTIP, ENABLERS, FILTER_PARAMS, FLOAT_REGEX, FULL_YEAR_MONTHS, IMPACT_PROPERTIES, IMPACT_SERIES_IDS_AND_LABELS, IN_COLORS, KPI_LABELS, LARGE_SCREEN_BREAKPOINT, LOCK_RESOURCE_NAMES, LOCK_RESOURCE_TEXTS, MONTH_TO_NUMBER, NUMBER_TO_MONTH, OES_WITH_DEPENDENT_FIELDS, OES_WITH_EXTENDED_LINE_OF_BUSINESS_OPTIONS, OE_KPI_POPULATE, PLANNED_IMPACT_PROPERTIES, QUARTERS, REPORTING_IMPACT_CURRENTLY_LOCKED_TOOLTIP, UNITS, applyFxRatesToInitiatives, arithmeticCalculation, calculateValue, convertCamelAndKebabCaseToTitle, convertImpactValuesToArray, convertImpactValuesToMap, convertImpactValuesUnit, countOccurrences, createEmptyInitiative, filterInitiatives, filterInitiativesOnDashboardPage, fireFilterEventWithTheProperPayloadBasedOnTrigger, formatDate, formatDateUTC, getColorsAndTextForInitiativePhaseChips, getImpactRangeForDates, getMonthAndYear, getQuarterAndYear, getUpdateChipColorBasedOnUpdateStatus, getUpdateStatus, hasImpactValue, linearApproximation, mapCommentsToArray, mapCommentsToYearAndQuarter, sortInitiatives, sortInitiativesByUid, sortString, whichActualsNeedToBeConfirmed };
